import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  Input,
  OnInit,
  Output,
  Self,
  TemplateRef,
  EventEmitter,
  ViewChild,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormControl,
  NgControl,
  Validators,
  FormControl,
  FormBuilder,
  AbstractControlOptions,
  FormControlStatus,
} from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { BehaviorSubject, Subscription, combineLatest } from 'rxjs';
import { isNil } from 'lodash';
import { PickerType } from '@danielmoncada/angular-datetime-picker';
import { OWL_DATE_TIME_FORMATS, OwlDateTimeComponent } from '@danielmoncada/angular-datetime-picker';
import { CombinedDatepickerRangeOutput, CombinedDateTimeRangeType } from '../../common/models';
import { AutoUnsubscribe } from '../../common/decorators';
import { MODIFIED_DATE_TIMEPICKER_CONFIG } from '../../common/constants/date-config';
import { DaysFromToday } from '../../store/constants';
import { FormStatus } from '../../common/constants/form-status';
import { IconNames } from '../../common/constants/icon-names';
import { fromGreaterThenToValidator, getLocalWallClockAsUTCTimestamp, ValidationService } from '../../common';
import { endOfDay, startOfDay } from 'date-fns';
import { TranslateModule } from '@ngx-translate/core';
import { DateModule } from '../../date.module';
import { IntrackFeatureModule } from '../../intrack-feature.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';

@Component({
  selector: 'ui-combined-date-time-range',
  imports: [DateModule, IntrackFeatureModule, TranslateModule, SvgIconComponent],
  templateUrl: './combined-date-time-range.component.html',
  styleUrls: ['./combined-date-time-range.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: OWL_DATE_TIME_FORMATS,
      useValue: MODIFIED_DATE_TIMEPICKER_CONFIG,
    },
  ],
})
@AutoUnsubscribe
export class CombinedDateTimeRangeComponent implements OnInit, ControlValueAccessor {
  @Input()
  label: string;
  @Input() pickerType: PickerType = 'both';
  @Output() isTodayToggled = new EventEmitter();

  @ContentChild('prefix') prefix: TemplateRef<HTMLElement>;
  @ContentChild('suffix') suffix: TemplateRef<HTMLElement>;

  @ViewChild('datetimePicker', { static: false }) datetimePicker?: OwlDateTimeComponent<Date>;
  @ViewChild('todaySelect', { static: false }) todaySelect?: MatSelect;

  @HostListener('document:click')
  onClickOutside(): void {
    if (this.isRelativeDateRangesVisible) {
      this.isRelativeDateRangesVisible = false;
    }
  }

  readonly minRelativeDaysValue = -90;
  readonly maxRelativeDaysValue = 90;
  readonly defaultRelativeDaysValue = 0;
  readonly defaultWorkingDaysValue = false;
  iconNames = IconNames;

  dateTimeRangeControl = new UntypedFormControl();
  isTodayOpened = false;
  daysFromToday = Object.values(DaysFromToday).filter((value: string | DaysFromToday) => !isNaN(Number(value)));
  combinedDateTimeRangeType = CombinedDateTimeRangeType;
  relativeDateRanges: FormControl<string> = new FormControl<string>(
    this.buildRelativeRangeInputText(this.defaultRelativeDaysValue, this.defaultRelativeDaysValue),
  );

  relativeDateRangesFormGroup = this.fb.group<{
    from: FormControl<number>;
    to: FormControl<number>;
    onlyWorkDays: FormControl<boolean>;
  }>(
    {
      from: this.fb.control<number>(0, [
        Validators.required,
        Validators.min(this.minRelativeDaysValue),
        Validators.max(this.maxRelativeDaysValue),
      ]),
      to: this.fb.control<number>(0, [
        Validators.required,
        Validators.min(this.minRelativeDaysValue),
        Validators.max(this.maxRelativeDaysValue),
      ]),
      onlyWorkDays: this.fb.control<boolean>(false),
    },
    { validator: fromGreaterThenToValidator } as AbstractControlOptions,
  );
  daysBeforeToday: BehaviorSubject<number> = new BehaviorSubject<number>(this.defaultRelativeDaysValue);
  daysAfterToday: BehaviorSubject<number> = new BehaviorSubject<number>(this.defaultRelativeDaysValue);
  onlyWorkingDays: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(this.defaultWorkingDaysValue);
  isRelativeDateRangesVisible = false;

  private subscriptions: Subscription[] = [];
  private combineDaysSubscription: Subscription = combineLatest([
    this.daysBeforeToday,
    this.daysAfterToday,
    this.onlyWorkingDays,
  ]).subscribe(([from, to, onlyWorkDays]: [number, number, boolean]) => {
    this.relativeDateRanges.reset(this.buildRelativeRangeInputText(from, to));
    if (this.onChange) {
      this.onChange({
        start: null,
        end: null,
        daysFromToday: { from, to, onlyWorkDays },
      });
    }
    this.dateTimeRangeControl.reset(null, { emitEvent: false });
  });

  private fromControlSubscription: Subscription = this.relativeDateRangesFormGroup
    .get('from')
    .valueChanges.subscribe((from: number) => {
      this.daysBeforeToday.next(from);
    });

  private toControlSubscription: Subscription = this.relativeDateRangesFormGroup
    .get('to')
    .valueChanges.subscribe((to: number) => this.daysAfterToday.next(to));

  private onlyWorkingDaysControlSubscription: Subscription = this.relativeDateRangesFormGroup
    .get('onlyWorkDays')
    .valueChanges.subscribe((onlyWorkingDays: boolean) => this.onlyWorkingDays.next(onlyWorkingDays));

  constructor(
    @Self() public ngControl: NgControl,
    public validationService: ValidationService,
    private cd: ChangeDetectorRef,
    private fb: FormBuilder,
  ) {
    ngControl.valueAccessor = this;
  }

  ngOnInit(): void {
    this.initRelativeDateRangesFormGroupStatusSubscription();
    this.initDateTimeRangeValueSubscription();
    this.subscriptions.push(
      this.combineDaysSubscription,
      this.fromControlSubscription,
      this.toControlSubscription,
      this.onlyWorkingDaysControlSubscription,
    );
  }

  registerOnChange(fn: () => CombinedDatepickerRangeOutput): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  writeValue(dateTimeRange: CombinedDatepickerRangeOutput): void {
    this.dateTimeRangeControl.setValue(
      [
        dateTimeRange?.start ? this.toPickerValue(dateTimeRange.start) : null,
        dateTimeRange?.end ? this.toPickerValue(dateTimeRange.end) : null,
      ],
      {
        emitEvent: false,
      },
    );
    if (!isNil(dateTimeRange?.daysFromToday)) {
      this.isTodayOpened = true;
      this.relativeDateRangesFormGroup.get('from').setValue(dateTimeRange.daysFromToday.from, {
        emitEvent: true,
      });
      this.relativeDateRangesFormGroup.get('to').setValue(dateTimeRange.daysFromToday.to, {
        emitEvent: true,
      });
      this.relativeDateRangesFormGroup.get('onlyWorkDays').setValue(dateTimeRange.daysFromToday.onlyWorkDays, {
        emitEvent: true,
      });
      this.cd.detectChanges();
    } else {
      this.relativeDateRanges.reset(null);
      this.resetRelativeDateRangesFormGroup({ emitEvent: false });
      this.isRelativeDateRangesVisible = false;
    }
  }

  open(event: MouseEvent, type: CombinedDateTimeRangeType): void {
    event.stopPropagation();
    if (type === CombinedDateTimeRangeType.TodayShift) {
      this.isTodayOpened = true;
      this.resetRelativeDateRangesFormGroup({ emitEvent: true });
      this.isRelativeDateRangesVisible = !this.isRelativeDateRangesVisible;
      this.cd.detectChanges();
    } else {
      this.isTodayOpened = false;
      this.relativeDateRanges.reset(null);
      this.resetRelativeDateRangesFormGroup({ emitEvent: false });
      this.ngControl.control.reset();
      this.cd.detectChanges();
      this.datetimePicker.open();
    }
  }

  toggleRelativeDateRangesVisibility(event: MouseEvent): void {
    event.stopPropagation();
    this.isRelativeDateRangesVisible = !this.isRelativeDateRangesVisible;
  }

  private onChange: (_: CombinedDatepickerRangeOutput) => void;

  private onTouched: () => void;

  private initDateTimeRangeValueSubscription(): void {
    const dateTimeRangeControlSubscription = this.dateTimeRangeControl.valueChanges.subscribe(
      (dateTimeRange: [Date, Date, { from: number; to: number; onlyWorkDays: boolean }]) => {
        const datePickerRange = new CombinedDatepickerRangeOutput();

        datePickerRange.start = startOfDay(this.fromPickerValue(dateTimeRange[0]));
        datePickerRange.end = endOfDay(this.fromPickerValue(dateTimeRange[1]));
        datePickerRange.daysFromToday = dateTimeRange[2];

        this.onChange(datePickerRange);
      },
    );

    this.subscriptions.push(dateTimeRangeControlSubscription);
  }

  private initRelativeDateRangesFormGroupStatusSubscription(): void {
    const relativeDateRangesFormGroupStatusSubscription: Subscription =
      this.relativeDateRangesFormGroup.statusChanges.subscribe((status: FormControlStatus) => {
        if (status === FormStatus.Invalid) {
          this.ngControl.control.setErrors({ fromGreaterThenTo: true });
        } else {
          this.ngControl.control.setErrors(null);
        }
      });

    this.subscriptions.push(relativeDateRangesFormGroupStatusSubscription);
  }

  private toPickerValue(value: Date | number): Date {
    return new Date(getLocalWallClockAsUTCTimestamp(value));
  }

  private fromPickerValue(value: Date): Date {
    const utc = new Date(value);

    return new Date(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
  }

  private resetRelativeDateRangesFormGroup(emitOption: { emitEvent: boolean }): void {
    this.relativeDateRangesFormGroup.get('from').reset(this.defaultRelativeDaysValue, emitOption);
    this.relativeDateRangesFormGroup.get('to').reset(this.defaultRelativeDaysValue, emitOption);
    this.relativeDateRangesFormGroup.get('onlyWorkDays').reset(this.defaultWorkingDaysValue, emitOption);
  }

  private buildRelativeRangeInputText(from: number, to: number): string {
    const getTextRangeValue = (value: number): string =>
      value < 0 ? `today - ${Math.abs(value)}` : value > 0 ? `today + ${value}` : 'today';
    return `From ${getTextRangeValue(from)} to ${getTextRangeValue(to)}`;
  }
}
